//
// ChatThanks
//
// The closing thanks a user leaves: the "thanks you and leaves" lead-in quoted as a
// thin outlined pill sitting behind the thanks itself, which renders as an ordinary
// message bubble. Same stack as the questionnaire interaction.
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { memo } from 'react';

import {
  Column,
  Row,
} from '../../../display';

import ChatMessageControls from './ChatMessageControls';
import {
  ChatAuthorIcon,
  ChatAuthorName,
  ChatDate,
  ChatItemClick,
  ChatSpacer,
  MessageFrame,
  MessageText,
} from './components';
import ContextPill from './ContextPill';

const ChatThanks = memo(({
  registerChild,
  translate,
  style,
  author,
  initials,
  crBlocked,
  color,
  gcatCode,
  message,
  visibleInThisGathering = true,
  // isScrolling,
  // isVisible,
  grpGpid,
  grpLbl,
  grpNbr,
  groupsMap,
  imagesMap,
  prtnrLogoUrl,
  selectGathering,
}) => {
  // const [showControls, setShowControls] = useState(false);
  const {
    id,
    gthrId,
    msgTxt,
    msgRdatim1,
  } = message;

  return (
    <div
      ref={registerChild}
      key={id}
      className={classNames(
        'w-full py-2 gap-1 flex flex-col relative items-center justify-center overflow-hidden group',
        {
          'px-6': visibleInThisGathering,
          'px-2 opacity-50': !visibleInThisGathering,
        },
      )}
      style={style}
    >
      <ChatItemClick
        gthrId={gthrId}
        selectGathering={selectGathering}
        visibleInThisGathering={visibleInThisGathering}
      />

      <ChatAuthorName
        translate={translate}
        author={author}
        initials={initials}
        crBlocked={crBlocked}
        gcatCode={gcatCode}
        grpNbr={grpNbr}
        prtnrLogoUrl={prtnrLogoUrl}
      />

      <Row
        start={!!author}
        end={!author}
        fullw
      >
        <ChatAuthorIcon
          author={author}
          initials={initials}
          color={color}
          gcatCode={gcatCode}
          grpGpid={grpGpid}
          grpLbl={grpLbl}
          groupsMap={groupsMap}
          imagesMap={imagesMap}
        />

        <div className={classNames(
          'order-3 flex flex-col min-w-0 max-w-[85%]',
          (author) ? 'items-start' : 'items-end',
        )}
        >
          <ContextPill
            author={author}
            text={`${translate('thanksYouAndLeaves', 'chat')}\u00A0:`}
          />

          {(msgTxt)
            ? (
                <div className={classNames(
                  'relative -mt-1',
                  (author) ? 'self-start' : 'self-end',
                )}
                >
                  <MessageFrame
                    author={author}
                    feedback
                    noSpacer
                    className="ring-2 ring-pxMsgsBgnd"
                  >
                    <MessageText
                      message={msgTxt}
                      author={author}
                    />
                  </MessageFrame>
                </div>
              )
            : null}
        </div>

        <Column
          className={classNames(
            'self-start',
            {
              'order-2': !author,
              'order-4': author,
            },
          )}
          gap={1}
          crossStart={author}
          crossEnd={!author}
        >
          <ChatDate
            dtime={msgRdatim1}
            myMessage={!author}
          />

          <ChatMessageControls
            translate={translate}
            message={message}
            author={author}
          />
        </Column>

        <ChatSpacer
          author={author}
        />

      </Row>
    </div>
  );
});
ChatThanks.displayName = 'ChatThanks';

ChatThanks.propTypes = {
  registerChild: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  author: PropTypes.string,
  initials: PropTypes.string,
  crBlocked: PropTypes.number,
  color: PropTypes.string,
  gcatCode: PropTypes.string,
  visibleInThisGathering: PropTypes.bool,
  // isScrolling: PropTypes.bool.isRequired,
  // isVisible: PropTypes.bool.isRequired,
  grpGpid: PropTypes.string,
  grpLbl: PropTypes.string,
  grpNbr: PropTypes.number,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
  imagesMap: PropTypes.instanceOf(Map).isRequired,
  selectGathering: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    gthrId: PropTypes.number,
    msgTxt: PropTypes.string,
    msgRdatim1: PropTypes.string.isRequired,
  }).isRequired,
  prtnrLogoUrl: PropTypes.string,
};

export default ChatThanks;
